import { Fragment, FC, MouseEvent, memo } from 'react';

import { makeStyles } from '@material-ui/styles';
import {
    CloseFill,
    UserFullName,
    UserJobDetails,
    UserIcon,
    Tag,
    LinkedInIcon,
    TwitterIcon,
    IconButton,
    Text,
    Theme,
    UserSocialLink,
} from '@bizzabo/design-system';
import { UserPreview } from '@bizzabo/entities';
import { useMessagesEnabled } from 'meetings/messages';

import { useTruncateSummary } from '../UserProfile/hooks';
import { default as MessageButton, OnButtonClickCallback } from './MessageButton';

type UserInfoViewProps = {
    user: UserPreview;
    showCloseButton?: boolean;
    onClose?: (event: MouseEvent) => void;
    onButtonClick?: OnButtonClickCallback;
    isUserModerator?: boolean;
    onTwitterClick?: () => void;
    onLinkedInClick?: () => void;
};

const UserInfoView: FC<UserInfoViewProps> = ({
    user,
    showCloseButton = true,
    onClose = () => {},
    isUserModerator = false,
    onButtonClick,
    onTwitterClick = () => {},
    onLinkedInClick = () => {},
}) => {
    const classes = useStyles();
    const summary = useTruncateSummary(user?.summary);
    const isMessagesEnabled = useMessagesEnabled();

    const isProfileHidden = user?.isHidden;
    const isSummaryVisible = summary && !isProfileHidden;
    const userCompanyWebsite = !isProfileHidden ? user?.web : '';

    return (
        <Fragment>
            {showCloseButton ? (
                <IconButton
                    icon={CloseFill}
                    data-testid="close-popup-icon"
                    className={classes.closeButton}
                    size="small"
                    onClick={onClose}
                />
            ) : null}
            <div className={classes.header}>
                <UserIcon size="X-Large" photoUrl={user.photoUrl} />
                <div className={classes.userInfo}>
                    <div className={classes.fitContainer}>
                        <UserFullName
                            firstName={user.firstName}
                            lastName={user.lastName}
                            clamp={1}
                            variant="t60"
                            className={classes.userFullName}
                        />
                        {isUserModerator && <Tag tagText="Host" className={classes.tag} />}
                    </div>
                    <UserJobDetails
                        role={user.title}
                        company={user.company}
                        web={userCompanyWebsite}
                        variant="t81"
                        clamp={2}
                        disablePortals
                    />
                </div>
            </div>
            {isSummaryVisible && (
                <Text variant="t81" className={classes.userSummary}>
                    {summary}
                </Text>
            )}
            <div className={classes.buttonSection}>
                <div className={classes.socialLinks}>
                    {user?.linkedinId && (
                        <UserSocialLink
                            referTo={user.linkedinId}
                            onLinkClick={onLinkedInClick}
                            disabled={isProfileHidden}
                        >
                            <LinkedInIcon />
                        </UserSocialLink>
                    )}
                    {user?.twitterHandle && (
                        <UserSocialLink
                            referTo={`https://twitter.com/${user.twitterHandle}`}
                            onLinkClick={onTwitterClick}
                            disabled={isProfileHidden}
                        >
                            <TwitterIcon />
                        </UserSocialLink>
                    )}
                </div>
                {isMessagesEnabled && <MessageButton user={user} onButtonClick={onButtonClick} />}
            </div>
            {isProfileHidden && (
                <Text className={classes.hiddenProfileText} variant="t81">
                    Messaging is disabled for hidden profiles
                </Text>
            )}
        </Fragment>
    );
};

UserInfoView.displayName = 'UserInfoView';

const useStyles = makeStyles<Theme>(
    theme => ({
        header: {
            display: 'grid',
            gridTemplateColumns: '50px calc(100% - 58px)', // 50px icon fixed width + column gap
            columnGap: 8,
            alignItems: 'center',
        },
        userInfo: {
            display: 'flex',
            flexDirection: 'column',
        },
        fitContainer: {
            display: 'grid',
            gridTemplateColumns: 'fit-content(100%) min-content',
            columnGap: 4,
            alignItems: 'center',
        },
        tag: {
            backgroundColor: theme.customization.interactive.C10,
        },
        userFullName: {
            lineHeight: '18px',
        },
        userSummary: {
            marginTop: 12,
            lineHeight: '150%',
            fontFamily: '"Inter", Arial, sans-serif',
        },
        buttonSection: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 12,
        },
        socialLinks: {
            display: 'flex',
            alignItems: 'center',
        },
        closeButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: theme.palette.greys[400],
            color: theme.palette.greys[100],
            width: '2rem',
            height: '2rem',
            fontSize: '1.4rem',
            mixBlendMode: 'initial',
            '&:hover': {
                color: theme.palette.greys[100],
            },
        },
        hiddenProfileText: {
            marginTop: 12,
            color: theme.palette.greys[400],
        },
    }),
    { classNamePrefix: 'people-user-info-view' }
);

export default memo(UserInfoView);
